import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NTA2026SelfServiceCard from './NTA2026SelfServiceCard';
import MyTaxCard from './MyTaxCard';
import LeavePolicyAssistCard from './LeavePolicyAssistCard';
import EwaCard from './EwaCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, Stack, Chip, Avatar,
  Skeleton, Button, IconButton, LinearProgress, alpha, Dialog,
  DialogContent, DialogActions, TextField, MenuItem, FormControlLabel,
  Switch, Tabs, Tab, InputAdornment, Tooltip,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  Campaign as AnnouncementIcon,
  PushPin as PinIcon,
  CalendarMonth as HolidayIcon,
  AccessTime as LeaveIcon,
  Description as PayslipIcon,
  Cake as BirthdayIcon,
  Person as PersonIcon,
  Notifications as NotifIcon,
  ArrowForward as ArrowIcon,
  Circle as UnreadDot,
  Add as AddIcon,
  Refresh as RefreshIcon,
  AutoAwesome as GenerateIcon,
  Search as SearchIcon,
  People as DirectoryIcon,
  EventAvailable as RequestLeaveIcon,
  Receipt as ViewPayslipIcon,
  Edit as EditProfileIcon,
  Assignment as OnboardingIcon,
  Security as WithholdingIcon,
  Link as LinkIcon,
  ReceiptLong as ExpenseClaimIcon,
  ShoppingCartOutlined as PurchaseIcon,
  Lightbulb as SuggestIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS, MODULE_COLORS } from '@/theme/colorTokens';
import { usePaletteAccents } from '@/hooks/usePaletteAccents';
import { useJurisdiction } from '@/hooks/useJurisdiction';
import { formatCurrency, formatDate, resolveUploadUrl } from '@/utils/format';
import { useAuthStore } from '@/stores/authStore';
import { usePermission } from '@/hooks/usePermission';
import { useNotificationStore } from '@/stores/notificationStore';
import { dashboardApi, UpcomingHoliday } from '@/api/endpoints/dashboard';
import apiClient from '@/api/client';
import { notificationsApi, NotificationResponse } from '@/api/endpoints/notifications';
import { messagingApi, UpcomingGreeting } from '@/api/endpoints/messaging';
import PageHelpIcon from '@/components/PageHelpIcon';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import PageIllustration from '@/components/PageIllustration';
import { employeesApi, onboardingApi, portalFinanceApi, DeductionResponse } from '@/api/endpoints/hr';

// ── Types ──

interface Announcement {
  id: string;
  title: string;
  body: string;
  category?: string;
  priority?: string;
  is_pinned: boolean;
  posted_by_name?: string;
  source?: string;
  target_departments?: string[] | null;
  target_roles?: string[] | null;
  created_at: string;
}

interface PortalData {
  profile: {
    id: string;
    first_name: string;
    last_name: string;
    employee_ref: string;
    designation?: string;
    annual_rent_paid?: string | null;
    nhf_opt_in?: boolean;
  };
  leave_balances: Array<{
    leave_type: string;
    balance: number;
    used: number;
    remaining: number;
  }>;
  recent_payslips: Array<{
    id: string;
    period_year: number;
    period_month: number;
    net_pay: number;
  }>;
  holidays: Array<{
    id: string;
    holiday: { name: string };
    observed_date: string;
    is_full_day: boolean;
  }>;
}

// ── Constants ──

const PRIORITY_COLORS: Record<string, 'error' | 'warning' | 'default'> = {
  urgent: 'error',
  high: 'warning',
};

// CATEGORY_LABELS & SOURCE_LABELS moved inside component for palette awareness

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'policy', label: 'Policy Update' },
  { value: 'event', label: 'Event' },
  { value: 'hr', label: 'People' },
  { value: 'safety', label: 'Safety' },
  { value: 'it', label: 'IT / Systems' },
];

const PRIORITIES = [
  { value: '', label: 'Normal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// ── Helpers ──

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(dateStr);
}

// ── Compose Dialog ──

function ComposeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();
  const [form, setForm] = useState({
    title: '', body: '', category: 'general', priority: '',
    is_pinned: false, target_departments: null as string[] | null, target_roles: null as string[] | null,
  });

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post('/settings/announcements', data),
    onSuccess: () => {
      showSuccess('Post published');
      queryClient.invalidateQueries({ queryKey: ['portal-announcements'] });
      queryClient.invalidateQueries({ queryKey: ['announcements-active'] });
      onClose();
      setForm({ title: '', body: '', category: 'general', priority: '', is_pinned: false, target_departments: null, target_roles: null });
    },
    onError: (err: any) => showError(err?.response?.data?.detail || 'Failed to publish'),
  });

  const handleSubmit = () => {
    if (!form.title.trim() || !form.body.trim()) {
      showError('Title and body are required');
      return;
    }
    mutation.mutate({
      title: form.title,
      body: form.body,
      category: form.category,
      priority: form.priority || undefined,
      is_pinned: form.is_pinned,
      target_departments: form.target_departments,
      target_roles: form.target_roles,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BrandedDialogTitle>New Announcement</BrandedDialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label="Title" value={form.title} required
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Body" value={form.body} required
          onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
          multiline rows={4}
        />
        <Stack direction="row" spacing={2}>
          <TextField
            select label="Category" value={form.category} size="small" sx={{ minWidth: 160 }}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
          >
            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
          </TextField>
          <TextField
            select label="Priority" value={form.priority} size="small" sx={{ minWidth: 140 }}
            onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}
          >
            {PRIORITIES.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
          </TextField>
        </Stack>
        <FormControlLabel
          control={<Switch checked={form.is_pinned} onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))} size="small" />}
          label="Pin to top"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={mutation.isPending}>
          {mutation.isPending ? 'Publishing...' : 'Publish'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Quick Actions ──

function QuickActions({ onSwitchToDirectory, hasEmployeeRecord, hasHrModule, hasLeavePermission, canPurchase, canSuggest }: {
  onSwitchToDirectory: () => void;
  hasEmployeeRecord: boolean;
  hasHrModule: boolean;
  hasLeavePermission: boolean;
  canPurchase: boolean;
  canSuggest: boolean;
}) {
  const ACCENT = usePaletteAccents();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const actions = [
    {
      label: 'Request Leave', desc: 'Submit a new request',
      icon: <RequestLeaveIcon sx={{ fontSize: 18 }} />, color: STATUS_COLORS.success,
      onClick: () => navigate('/profile', { state: { tab: 3 } }),
      disabled: !hasEmployeeRecord || !hasLeavePermission,
      tooltip: !hasEmployeeRecord ? 'No employee record linked to your account' : !hasLeavePermission ? 'You don\'t have leave permissions' : '',
    },
    {
      label: 'Payslips', desc: 'View your pay history',
      icon: <ViewPayslipIcon sx={{ fontSize: 18 }} />, color: ACCENT.blue,
      onClick: () => navigate('/profile', { state: { tab: 3, subTab: 1 } }),
      disabled: !hasEmployeeRecord,
      tooltip: !hasEmployeeRecord ? 'No employee record linked to your account' : '',
    },
    {
      label: 'My Profile', desc: 'View & edit your details',
      icon: <EditProfileIcon sx={{ fontSize: 18 }} />, color: ACCENT.purple,
      onClick: () => navigate('/profile'),
      disabled: false, tooltip: '',
    },
    {
      label: 'Submit Expense', desc: 'Claim travel & expenses',
      icon: <ExpenseClaimIcon sx={{ fontSize: 18 }} />, color: STATUS_COLORS.info,
      onClick: () => navigate('/hr/expense-claims/mine'),
      disabled: !hasEmployeeRecord,
      tooltip: !hasEmployeeRecord ? 'No employee record linked to your account' : '',
    },
    {
      label: 'Directory', desc: 'Find a colleague',
      icon: <DirectoryIcon sx={{ fontSize: 18 }} />, color: STATUS_COLORS.warning,
      onClick: onSwitchToDirectory,
      disabled: !hasHrModule,
      tooltip: !hasHrModule ? 'HR module is not enabled' : '',
    },
    // Budget build 2026-10-24: purchase requests and budget suggestions from the portal.
    ...(canPurchase ? [{
      label: t('bud.portal.purchase', 'Request a purchase'), desc: t('bud.portal.purchase_desc', 'Ask for something to be bought'),
      icon: <PurchaseIcon sx={{ fontSize: 18 }} />, color: ACCENT.blue,
      onClick: () => navigate('/portal/purchases/new'),
      disabled: false, tooltip: '',
    }] : []),
    ...(canSuggest ? [{
      label: t('bud.portal.suggest', 'Suggest a budget item'), desc: t('bud.portal.suggest_desc', 'Ideas for next year’s budget'),
      icon: <SuggestIcon sx={{ fontSize: 18 }} />, color: ACCENT.purple,
      onClick: () => navigate('/portal/budget-suggestions/new'),
      disabled: false, tooltip: '',
    }] : []),
    {
      label: t('bud.portal.notify', 'Notifications'), desc: t('bud.portal.notify_desc', 'Choose your alerts and channels'),
      icon: <NotifIcon sx={{ fontSize: 18 }} />, color: ACCENT.blue,
      onClick: () => navigate('/profile/notifications'),
      disabled: false, tooltip: '',
    },
  ];

  return (
    <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
      {actions.map((a) => {
        const card = (
          <Box
            onClick={a.disabled ? undefined : a.onClick}
            role="button"
            tabIndex={a.disabled ? -1 : 0}
            onKeyDown={(e) => { if (!a.disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); a.onClick(); } }}
            aria-label={a.label}
            aria-disabled={a.disabled}
            sx={{
              p: 1.25, borderRadius: '10px',
              border: '1px solid',
              borderColor: a.disabled ? 'action.disabledBackground' : 'divider',
              bgcolor: 'background.paper',
              cursor: a.disabled ? 'default' : 'pointer',
              opacity: a.disabled ? 0.45 : 1,
              height: '100%',
              display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1.25,
              transition: 'all 0.2s ease',
              '&:hover': a.disabled ? {} : {
                borderColor: a.color,
                bgcolor: `${a.color}08`,
                transform: 'translateY(-2px)',
                boxShadow: `0 4px 14px ${a.color}1A`,
              },
              '&:focus-visible': {
                outline: `2px solid ${a.color}`,
                outlineOffset: 2,
              },
              '&:active': a.disabled ? {} : { transform: 'translateY(0)' },
            }}
          >
            <Box
              sx={{
                width: 32, height: 32, borderRadius: '8px',
                bgcolor: `${a.color}14`, color: a.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {a.icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.bold, color: 'text.primary', lineHeight: 1.2 }}>
                {a.label}
              </Typography>
              <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                {a.desc}
              </Typography>
            </Box>
          </Box>
        );
        return (
          <Grid key={a.label} size={{ xs: 6, sm: 3 }}>
            {a.disabled && a.tooltip ? (
              <Tooltip title={a.tooltip} arrow placement="bottom">
                <span style={{ display: 'block', height: '100%' }}>{card}</span>
              </Tooltip>
            ) : card}
          </Grid>
        );
      })}
    </Grid>
  );
}

// ── Worker Directory ──

function WorkerDirectory() {
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['employees', 0, ''],
    queryFn: () => employeesApi.list({ per_page: 100 }),
    select: (res) => res.data?.items || [],
  });

  const employees = useMemo(() => {
    if (!data) return [];
    const q = search.toLowerCase();
    return data.filter((e: any) =>
      !q ||
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      (e.designation || '').toLowerCase().includes(q) ||
      (e.email || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <Box>
      <TextField
        fullWidth
        placeholder="Search employees by name, role, or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} /></InputAdornment>,
        }}
      />

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={60} />)}</Stack>
      ) : employees.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <DirectoryIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">{search ? 'No employees match your search' : 'No employees found'}</Typography>
        </Box>
      ) : (
        <Card variant="outlined">
          {employees.map((emp: any, idx: number) => (
            <Stack
              key={emp.id}
              direction="row" spacing={1.5} alignItems="center"
              sx={{
                px: 2, py: 1,
                borderBottom: idx < employees.length - 1 ? '1px solid' : 'none',
                borderColor: 'divider',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                src={resolveUploadUrl(emp.avatar_url)}
                sx={{ width: 30, height: 30, bgcolor: 'primary.main', fontSize: TYPE.xs, fontWeight: TYPE.semibold }}
              >
                {`${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`.toUpperCase()}
              </Avatar>
              <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold, minWidth: 140 }} noWrap>
                {emp.first_name} {emp.last_name}
              </Typography>
              <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', flex: 1 }} noWrap>
                {emp.designation || '—'}
              </Typography>
              {emp.email && (
                <Typography sx={{ fontSize: TYPE.xs, color: 'text.disabled' }} noWrap>
                  {emp.email}
                </Typography>
              )}
            </Stack>
          ))}
        </Card>
      )}
    </Box>
  );
}

// ── Main Page ──

export default function PortalPage() {
  const ACCENT = usePaletteAccents();
  const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
    general: { label: 'General', color: STATUS_COLORS.default },
    policy: { label: 'Policy', color: ACCENT.blue },
    event: { label: 'Event', color: ACCENT.purple },
    hr: { label: 'People', color: STATUS_COLORS.success },
    safety: { label: 'Safety', color: STATUS_COLORS.warning },
    it: { label: 'IT', color: MODULE_COLORS.comms },
  };
  const SOURCE_LABELS: Record<string, { label: string; color: string }> = {
    auto_birthday: { label: 'Birthday', color: ACCENT.pink },
    auto_anniversary: { label: 'Anniversary', color: ACCENT.blue },
    auto_holiday: { label: 'Holiday', color: STATUS_COLORS.warning },
    auto_news_external: { label: 'Industry News', color: MODULE_COLORS.comms },
    auto_news_internal: { label: 'Business Pulse', color: STATUS_COLORS.success },
  };
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const commsEnabled = useAuthStore((s) => s.getEnabledModules()).includes('comms');
  const { hasPermission } = usePermission();
  const { currencyCode } = useJurisdiction();
  const canPost = hasPermission('portal.write');
  const [composeOpen, setComposeOpen] = useState(false);
  const [showAllAnnouncements, setShowAllAnnouncements] = useState(false);
  const [portalTab, setPortalTab] = useState(0);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotificationStore();

  const generateMutation = useMutation({
    mutationFn: (period: string) =>
      apiClient.post('/settings/portal/generate', null, { params: { period } }),
    onSuccess: (res) => {
      const d = res.data as Record<string, number>;
      showSuccess(`Generated ${d.total_new || 0} new posts`);
      queryClient.invalidateQueries({ queryKey: ['portal-announcements'] });
    },
    onError: () => showError('Failed to generate content'),
  });

  const refreshNewsMutation = useMutation({
    mutationFn: (period: string) =>
      apiClient.post('/settings/portal/refresh-news', null, { params: { period } }),
    onSuccess: (res) => {
      const d = res.data as Record<string, number>;
      showSuccess(`${d.news_posted || 0} news items refreshed`);
      queryClient.invalidateQueries({ queryKey: ['portal-announcements'] });
    },
    onError: () => showError('Failed to refresh news'),
  });

  const currentPeriod = new Date().getHours() < 14 ? 'morning' : 'afternoon';

  // Announcements — filtered by user's audience
  const { data: announcementsData, isLoading: annLoading } = useQuery<{ items: Announcement[]; total: number }>({
    queryKey: ['portal-announcements'],
    queryFn: () => apiClient.get('/settings/announcements', { params: { active_only: true, filter_audience: true } }).then((r) => r.data),
  });

  // Employee portal data (may fail for non-employees — that's ok)
  const { data: portalData } = useQuery<PortalData>({
    queryKey: ['employee-portal'],
    queryFn: () => apiClient.get<PortalData>('/employee/my-info').then((r) => r.data),
    retry: false,
  });

  // Notifications (recent 5)
  const { data: notifsData } = useQuery({
    queryKey: ['notifications', undefined, 1],
    queryFn: () => notificationsApi.list({ per_page: 5 }).then((r) => r.data),
  });

  // Onboarding instance (for progress card)
  const { data: onboardingInstance } = useQuery({
    queryKey: ['my-onboarding'],
    queryFn: () => onboardingApi.getMyOnboarding().then((r) => r.data),
    retry: false,
  });

  // Upcoming birthdays/anniversaries — comms module only.
  const { data: upcomingEvents } = useQuery<UpcomingGreeting[]>({
    queryKey: ['messaging/upcoming', 14],
    queryFn: () => messagingApi.getUpcoming(14).then((r) => r.data),
    retry: false,
    enabled: commsEnabled,
  });

  // Upcoming holidays (independent endpoint — works without employee record)
  const { data: holidaysData } = useQuery<UpcomingHoliday[]>({
    queryKey: ['dashboard/upcoming-holidays'],
    queryFn: () => dashboardApi.upcomingHolidays().then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });

  // Retention withholdings (staff self-service — may fail for non-employees)
  const { data: myWithholdingsData } = useQuery({
    queryKey: ['employee-withholdings'],
    queryFn: () => portalFinanceApi.getMyWithholdings().then((r) => r.data),
    retry: false,
  });
  const myWithholdings: DeductionResponse[] = myWithholdingsData?.items || [];

  // Derived data
  const announcements = useMemo(() => {
    const items = announcementsData?.items || [];
    return [...items].sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [announcementsData]);

  // Use independent holidays endpoint (always available), fall back to portalData
  const upcomingHolidays = useMemo(() => {
    if (holidaysData && holidaysData.length > 0) {
      return holidaysData.slice(0, 5).map((h) => ({
        id: h.id,
        holiday: { name: h.name },
        observed_date: h.observed_date,
        is_full_day: h.is_full_day,
      }));
    }
    if (!portalData?.holidays) return [];
    const today = new Date();
    return portalData.holidays
      .filter((h) => new Date(h.observed_date) >= today)
      .sort((a, b) => new Date(a.observed_date).getTime() - new Date(b.observed_date).getTime())
      .slice(0, 5);
  }, [holidaysData, portalData?.holidays]);

  const totalLeave = portalData?.leave_balances?.reduce((s, lb) => s + lb.remaining, 0) ?? null;
  const latestPayslip = portalData?.recent_payslips?.[0];
  const notifications: NotificationResponse[] = notifsData?.items || [];
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const events = (upcomingEvents || []).slice(0, 6);
  const hasEmployeeRecord = !!portalData?.profile;

  // Module & permission checks for quick actions
  const enabledModules = useAuthStore((s) => s.getEnabledModules());
  const hasHrModule = enabledModules.includes('hr');
  const hasLeavePermission = hasPermission('hr.leave.read') || hasPermission('hr.leave.write');

  return (
    <Box>
      {/* Hero header with illustration */}
      <PageIllustration
        context="portal"
        greeting={`Welcome${portalData?.profile ? `, ${portalData.profile.first_name}` : user?.first_name ? `, ${user.first_name}` : ''}`}
        subtitle="Your company hub — announcements, quick access, and what's coming up"
        maxHeight={90}
      />

      {/* Tabs + admin actions inline */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Tabs
          value={portalTab}
          onChange={(_, v) => setPortalTab(v)}
          sx={{
            minHeight: 36, flex: 1,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: TYPE.semibold, fontSize: TYPE.md, minHeight: 36, py: 0.5 },
          }}
        >
          <Tab label="Feed" />
          <Tab label="Directory" icon={<DirectoryIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
        {canPost && (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', ml: 1 }}>
            <Tooltip title="Refresh News" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => refreshNewsMutation.mutate(currentPeriod)}
                  disabled={refreshNewsMutation.isPending}
                  sx={{ width: 28, height: 28 }}
                >
                  <RefreshIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Generate All" arrow>
              <span>
                <IconButton
                  size="small"
                  onClick={() => generateMutation.mutate(currentPeriod)}
                  disabled={generateMutation.isPending}
                  sx={{ width: 28, height: 28 }}
                >
                  <GenerateIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="New Post" arrow>
              <IconButton
                size="small"
                onClick={() => setComposeOpen(true)}
                sx={{
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                  width: 28, height: 28,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <PageHelpIcon pageKey="portal" />
          </Stack>
        )}
      </Box>

      {/* Quick Actions — always visible */}
      <QuickActions
        onSwitchToDirectory={() => setPortalTab(1)}
        hasEmployeeRecord={hasEmployeeRecord}
        hasHrModule={hasHrModule}
        hasLeavePermission={hasLeavePermission}
        canPurchase={hasPermission('purchasing.request')}
        canSuggest={hasPermission('budget.suggest')}
      />

      {/* Directory Tab */}
      {portalTab === 1 && <WorkerDirectory />}

      {/* Feed Tab */}
      {portalTab === 0 && (
        <Grid container spacing={2}>
          {/* Left Column */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* Announcements — max 3 visible, truncated */}
            <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <AnnouncementIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Announcements</Typography>
                    {announcements.length > 0 && (
                      <Chip label={announcements.length} size="small" sx={{ height: 20, fontSize: TYPE.xs, fontWeight: TYPE.semibold }} />
                    )}
                  </Stack>
                  {announcements.length > 3 && (
                    <Button
                      size="small"
                      endIcon={!showAllAnnouncements ? <ArrowIcon sx={{ fontSize: 14 }} /> : undefined}
                      onClick={() => setShowAllAnnouncements((v) => !v)}
                      sx={{ fontSize: TYPE.sm }}
                    >
                      {showAllAnnouncements ? 'Show Less' : 'View All'}
                    </Button>
                  )}
                </Stack>

                {annLoading ? (
                  <Stack spacing={1}>{[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={56} />)}</Stack>
                ) : announcements.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontSize: TYPE.md, py: 2, textAlign: 'center' }}>
                    No announcements at this time
                  </Typography>
                ) : (
                  <Stack spacing={0}>
                    {(showAllAnnouncements ? announcements : announcements.slice(0, 3)).map((a, idx) => {
                      const visibleCount = showAllAnnouncements ? announcements.length : Math.min(announcements.length, 3);
                      const cat = CATEGORY_LABELS[a.category || 'general'] || CATEGORY_LABELS.general;
                      return (
                        <Box
                          key={a.id}
                          sx={{
                            py: 1.5, px: 1.5, borderRadius: '6px',
                            borderBottom: idx < visibleCount - 1 ? '1px solid' : 'none',
                            borderColor: 'divider',
                            bgcolor: a.is_pinned ? (theme) => alpha(theme.palette.primary.main, 0.03) : 'transparent',
                          }}
                        >
                          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
                            {a.is_pinned && <PinIcon sx={{ fontSize: 14, color: 'primary.main', flexShrink: 0 }} />}
                            <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.semibold, flex: 1 }} noWrap>{a.title}</Typography>
                            <Chip
                              label={cat.label} size="small" variant="outlined"
                              sx={{ height: 20, fontSize: TYPE.xs, borderColor: cat.color, color: cat.color, flexShrink: 0 }}
                            />
                            {a.priority && PRIORITY_COLORS[a.priority] && (
                              <Chip label={a.priority} size="small" color={PRIORITY_COLORS[a.priority]} variant="outlined" sx={{ height: 20, fontSize: TYPE.xs }} />
                            )}
                          </Stack>
                          <Typography sx={{
                            fontSize: TYPE.base, color: 'text.secondary', lineHeight: 1.6,
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                          }}>
                            {a.body.replace(/<[^>]*>/g, '')}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75 }}>
                            {a.source && SOURCE_LABELS[a.source] && (
                              <Chip
                                label={SOURCE_LABELS[a.source].label} size="small" variant="filled"
                                sx={{ height: 18, fontSize: TYPE.xs, bgcolor: SOURCE_LABELS[a.source].color, color: 'common.white' }}
                              />
                            )}
                            <Typography sx={{ fontSize: TYPE.xs, color: 'text.disabled' }}>
                              {formatTime(a.created_at)}
                            </Typography>
                            <Typography
                              component="span"
                              onClick={() => navigate(`/portal/announcements/${a.id}`)}
                              sx={{ fontSize: TYPE.xs, color: 'primary.main', cursor: 'pointer', ml: 'auto', '&:hover': { textDecoration: 'underline' } }}
                            >
                              Read more
                            </Typography>
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Recent Notifications */}
            <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <NotifIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold }}>Notifications</Typography>
                    {unreadCount > 0 && (
                      <Chip label={unreadCount} size="small" color="error" sx={{ height: 20, fontSize: TYPE.xs, fontWeight: TYPE.semibold }} />
                    )}
                  </Stack>
                  <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/notifications')} sx={{ fontSize: TYPE.sm }}>
                    View All
                  </Button>
                </Stack>

                {notifications.length === 0 ? (
                  <Typography color="text.secondary" sx={{ fontSize: TYPE.md }}>No notifications yet</Typography>
                ) : (
                  <Stack spacing={0}>
                    {notifications.slice(0, 4).map((n, idx) => (
                      <Stack
                        key={n.id}
                        direction="row" spacing={0.75} alignItems="center"
                        sx={{
                          py: 1,
                          borderBottom: idx < Math.min(notifications.length, 4) - 1 ? '1px solid' : 'none',
                          borderColor: 'divider',
                        }}
                      >
                        {!n.is_read && <UnreadDot sx={{ fontSize: 7, color: 'primary.main', flexShrink: 0 }} />}
                        <Typography sx={{ fontSize: TYPE.md, fontWeight: n.is_read ? 400 : 600, flex: 1 }} noWrap>
                          {n.title}
                        </Typography>
                        <Typography sx={{ fontSize: TYPE.xs, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                          {formatTime(n.created_at)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column */}
          <Grid size={{ xs: 12, md: 5 }}>
            {/* Onboarding Progress */}
            {onboardingInstance && onboardingInstance.status === 'in_progress' && (
              <Card sx={{ mb: 2, border: '1px solid', borderColor: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03) }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <OnboardingIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>My Onboarding</Typography>
                    </Stack>
                    <Button
                      size="small"
                      endIcon={<ArrowIcon sx={{ fontSize: 14 }} />}
                      onClick={() => navigate('/my-onboarding')}
                      sx={{ fontSize: TYPE.sm }}
                    >
                      View Tasks
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                        {onboardingInstance.completed_tasks || 0} of {onboardingInstance.total_tasks || 0} tasks complete
                      </Typography>
                      <Typography sx={{ fontSize: TYPE.lg, fontWeight: TYPE.bold, color: 'primary.main' }}>
                        {Math.round(onboardingInstance.progress_pct || 0)}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={onboardingInstance.progress_pct || 0}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': { borderRadius: 4 },
                      }}
                    />
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Payslip + Leave */}
            <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Latest Payslip */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <PayslipIcon sx={{ fontSize: 18, color: 'success.main' }} />
                    <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Latest Payslip</Typography>
                  </Stack>
                  {latestPayslip && (
                    <Button size="small" onClick={() => navigate('/profile', { state: { tab: 3, subTab: 1 } })} sx={{ fontSize: TYPE.sm, minWidth: 0, p: 0 }}>
                      Details
                    </Button>
                  )}
                </Stack>
                {latestPayslip ? (
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ px: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: TYPE.xl, fontWeight: TYPE.bold }}>
                      {formatCurrency(latestPayslip.net_pay, currencyCode)}
                    </Typography>
                    <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                      {new Date(latestPayslip.period_year, latestPayslip.period_month - 1).toLocaleString('default', { month: 'long' })} {latestPayslip.period_year}
                    </Typography>
                  </Stack>
                ) : (
                  <Typography sx={{ fontSize: TYPE.base, color: 'text.disabled', px: 1 }}>
                    {hasEmployeeRecord ? 'No payslips available yet' : 'No employee record linked'}
                  </Typography>
                )}

                {/* Leave Balances */}
                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.5, pt: 1.5 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <LeaveIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Leave</Typography>
                      {totalLeave !== null && (
                        <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary' }}>
                          ({totalLeave.toFixed(1)} days left)
                        </Typography>
                      )}
                    </Stack>
                    {hasEmployeeRecord && hasLeavePermission && (
                      <Button size="small" onClick={() => navigate('/profile', { state: { tab: 3 } })} sx={{ fontSize: TYPE.sm, minWidth: 0, p: 0 }}>
                        Apply
                      </Button>
                    )}
                  </Stack>
                  {portalData?.leave_balances && portalData.leave_balances.length > 0 ? (
                    <Stack spacing={1}>
                      {portalData.leave_balances.map((lb) => {
                        const pct = lb.balance > 0 ? (lb.used / lb.balance) * 100 : 0;
                        return (
                          <Stack key={lb.leave_type} direction="row" spacing={1.5} alignItems="center">
                            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium, minWidth: 80 }} noWrap>{lb.leave_type}</Typography>
                            <LinearProgress
                              variant="determinate" value={Math.min(pct, 100)}
                              sx={{
                                flex: 1, height: 5, borderRadius: 3, bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 3,
                                  bgcolor: lb.remaining >= lb.balance * 0.5 ? STATUS_COLORS.success : lb.remaining >= lb.balance * 0.25 ? ACCENT.amber : STATUS_COLORS.error,
                                },
                              }}
                            />
                            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold, minWidth: 28, textAlign: 'right', color: lb.remaining > 0 ? 'success.main' : 'error.main' }}>
                              {lb.remaining.toFixed(0)}
                            </Typography>
                          </Stack>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography sx={{ fontSize: TYPE.base, color: 'text.disabled', px: 1 }}>
                      {hasEmployeeRecord ? 'No leave balances configured' : 'No employee record linked'}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>

            {/* Tax Benefits — Rent Relief + NHF (Phase 9) */}
            {hasEmployeeRecord && (
              <Box sx={{ mb: 2 }}>
                <NTA2026SelfServiceCard
                  annualRentPaid={portalData?.profile?.annual_rent_paid ?? null}
                  nhfOptIn={portalData?.profile?.nhf_opt_in ?? true}
                />
              </Box>
            )}

            {/* My Tax — YTD PAYE + reliefs + downloadable tax certificate */}
            {hasEmployeeRecord && portalData?.profile?.id && (
              <Box sx={{ mb: 2 }}>
                <MyTaxCard
                  employeeId={portalData.profile.id}
                  annualRentPaid={portalData?.profile?.annual_rent_paid ?? null}
                  nhfOptIn={portalData?.profile?.nhf_opt_in ?? true}
                />
              </Box>
            )}

            {/* Earned Wage Access — available-to-draw + request + history (P07) */}
            {hasEmployeeRecord && (
              <Box sx={{ mb: 2 }}>
                <EwaCard />
              </Box>
            )}

            {/* Leave policy Q&A — grounded in admin-uploaded policy doc */}
            {hasEmployeeRecord && (
              <Box sx={{ mb: 2 }}>
                <LeavePolicyAssistCard />
              </Box>
            )}

            {/* My Withholdings — retention plans, refunded on exit */}
            {hasEmployeeRecord && myWithholdings.length > 0 && (
              <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1 }}>
                    <WithholdingIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>
                      My Withholdings
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: TYPE.sm, color: 'text.secondary', mb: 1.5 }}>
                    A portion of your salary held each period, refunded in full
                    when you exit.
                  </Typography>
                  <Stack spacing={1.5}>
                    {myWithholdings.map((w) => {
                      const held = Number((w as { accumulated_amount?: string | number }).accumulated_amount || 0);
                      const target = Number(w.total_amount || 0);
                      const pct = target > 0 ? Math.min(100, (held / target) * 100) : 0;
                      const released = !!w.payout_paid_at;
                      const status = released ? 'Released' : w.is_active ? 'Active' : 'Paused';
                      const statusColor: 'default' | 'success' | 'warning' =
                        released ? 'default' : w.is_active ? 'success' : 'warning';
                      return (
                        <Box key={w.id}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }} noWrap>
                              {w.description || `${w.percentage ?? 0}% of basic`}
                            </Typography>
                            <Chip label={status} size="small" color={statusColor} sx={{ height: 20, fontSize: TYPE.xs }} />
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={released ? 100 : pct}
                            sx={{
                              height: 5, borderRadius: 3, bgcolor: 'action.hover',
                              '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                bgcolor: released ? STATUS_COLORS.success : ACCENT.blue,
                              },
                            }}
                          />
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                              {formatCurrency(held, currencyCode)} of {formatCurrency(target, currencyCode)}
                            </Typography>
                            {w.consent_document_url && (
                              <Button
                                size="small"
                                startIcon={<LinkIcon sx={{ fontSize: 12 }} />}
                                href={w.consent_document_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ fontSize: TYPE.xs, minWidth: 0, p: 0, textTransform: 'none' }}
                              >
                                Consent
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Events */}
            <Card sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                  <BirthdayIcon sx={{ fontSize: 18, color: 'secondary.main' }} />
                  <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Upcoming Events</Typography>
                </Stack>

                {events.length === 0 ? (
                  <Typography sx={{ fontSize: TYPE.base, color: 'text.disabled' }}>No upcoming birthdays or anniversaries</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {events.slice(0, 4).map((ev) => (
                      <Stack
                        key={`${ev.employee_id}-${ev.event_type}`}
                        direction="row" spacing={1} alignItems="center"
                        sx={{ py: 0.25 }}
                      >
                        <Avatar
                          src={resolveUploadUrl(ev.profile_photo)}
                          sx={{
                            width: 28, height: 28,
                            bgcolor: ev.event_type === 'birthday' ? ACCENT.pink : ACCENT.blue,
                            fontSize: TYPE.xs,
                          }}
                        >
                          {ev.profile_photo ? null : <PersonIcon sx={{ fontSize: 14 }} />}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.medium }} noWrap>
                            {ev.employee_name}
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                          {ev.event_type === 'birthday' ? 'Birthday' : `${ev.years}yr`}
                        </Typography>
                        <Chip
                          label={ev.days_until === 0 ? 'Today!' : `${ev.days_until}d`}
                          size="small"
                          color={ev.days_until === 0 ? 'error' : ev.days_until <= 3 ? 'warning' : 'default'}
                          sx={{ height: 20, fontSize: TYPE.xs, fontWeight: TYPE.semibold }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>

            {/* Upcoming Holidays */}
            <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.5 }}>
                  <HolidayIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  <Typography sx={{ fontSize: TYPE.md, fontWeight: TYPE.bold }}>Holidays</Typography>
                </Stack>

                {upcomingHolidays.length === 0 ? (
                  <Typography sx={{ fontSize: TYPE.base, color: 'text.disabled' }}>No upcoming holidays</Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {upcomingHolidays.slice(0, 3).map((h) => (
                      <Stack key={h.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: TYPE.sm, fontWeight: TYPE.semibold }}>{h.holiday.name}</Typography>
                          <Typography sx={{ fontSize: TYPE.xs, color: 'text.secondary' }}>
                            {formatDate(h.observed_date)}
                          </Typography>
                        </Box>
                        <Chip
                          label={h.is_full_day ? 'Full' : 'Half'}
                          size="small" variant={h.is_full_day ? 'filled' : 'outlined'}
                          sx={{ height: 20, fontSize: TYPE.xs }}
                        />
                      </Stack>
                    ))}
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Compose Dialog */}
      <ComposeDialog open={composeOpen} onClose={() => setComposeOpen(false)} />
    </Box>
  );
}
